import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { setDoc, doc, serverTimestamp } from "firebase/firestore";
import bfLogo from "../assets/bf_logo.png";
import { Eye, EyeOff } from "lucide-react"; 
import "./RegisterPage.css";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Kad nespaudytų 2 kartus

  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    vatId: "",
    phone: "",
    street: "",
    zip: "",
    city: "",
    email: "",
    password: "",
    confirmPassword: "",
    agb: false,
    privacy: false
  });

  const [error, setError] = useState("");
  const [vatStatus, setVatStatus] = useState("");

  // Pagalbinė funkcija gauti IP adresą (Teisiniam įrodymui)
  const getIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "unknown"; // Jei nepavyksta gauti IP, vis tiek registruojame
    }
  };

  const checkVat = async (vatNumber) => {
    if (!vatNumber) {
      setVatStatus("");
      return;
    }
    setVatStatus("loading");
    try {
      const cleanVat = vatNumber.replace(/\s+/g, "");
      // PATAISYTA: Dabar naudojamos `backticks`, kad veiktų kintamasis
      const url = `https://api.vatcomply.com/vat?vat_number=${cleanVat}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.valid) {
        setVatStatus("valid");
        if (data.name && !formData.companyName) {
          setFormData(prev => ({ ...prev, companyName: data.name }));
        }
      } else {
        setVatStatus("invalid");
      }
    } catch (err) {
      setVatStatus("");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // 1. Patikrinimas: Ar pažymėtos varnelės?
    if (!formData.agb || !formData.privacy) {
      setError("You must accept the Terms & Conditions and Privacy Policy to register.");
      setIsLoading(false);
      return;
    }

    // 2. Patikrinimas: Slaptažodžiai
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      // 3. Gauname IP adresą teisiniam "Audit Trail"
      const clientIp = await getIpAddress();
      const userAgent = navigator.userAgent; // Naršyklės info

      // 4. Sukuriame vartotoją Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // 5. Įrašome viską į duomenų bazę su TEISINIAIS ĮRODYMAIS
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: formData.name,
        companyName: formData.companyName,
        isVatRegistered: isVatRegistered,
        vatId: isVatRegistered ? formData.vatId : "Not applicable",
        phone: formData.phone,
        address: {
          street: formData.street,
          zip: formData.zip,
          city: formData.city
        },
        email: formData.email,
        role: "admin",
        subscriptionStatus: "trial",
        workerLimit: 5,
        adminLimit: 1,
        createdAt: serverTimestamp(),
        trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 dienų bandomasis
        
        // --- TEISINIS SKYRIUS (SVARBU TEISMUI) ---
        legal_consent: {
          agb_accepted: true,
          privacy_accepted: true,
          timestamp: serverTimestamp(), // Kada tiksliai paspaudė
          ip_address: clientIp,         // Iš kokio kompiuterio
          user_agent: userAgent,        // Kokia naršyklė
          agb_version: "v1.0-Jan2026",  // Kurią versiją pasirašė
          privacy_version: "v1.0-Jan2026"
        }
        // -----------------------------------------
      });

      alert("Account created successfully!");
      navigate("/admin");
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Ar mygtukas turi būti aktyvus?
  const isFormValid = formData.agb && formData.privacy;

  return (
    <div className="register-container">
      <div className="register-card">
        <img src={bfLogo} alt="Logo" className="register-logo" />
        <h2>BuildForce <span>AL</span></h2>
        <p className="subtitle">Start your 14-day free trial</p>

        {error && <p className="error-msg">{error}</p>}

        <form onSubmit={handleRegister}>
          <input 
            type="text" 
            placeholder="Full Name (Contact person)" 
            required={true}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
          />

          <div className="vat-selection">
            <label className="checkbox-item">
              <input 
                type="checkbox" 
                checked={isVatRegistered} 
                onChange={(e) => {
                  setIsVatRegistered(e.target.checked);
                  if (!e.target.checked) setVatStatus("");
                }} 
              />
              <span>My company is VAT registered in EU</span>
            </label>
          </div>

          {isVatRegistered && (
            <div className="input-group">
              <input 
                type="text" 
                placeholder="Enter VAT ID" 
                required={isVatRegistered}
                value={formData.vatId}
                onChange={(e) => setFormData({...formData, vatId: e.target.value})}
                onBlur={(e) => checkVat(e.target.value)}
              />
              {vatStatus === "loading" && <span className="vat-hint">Checking...</span>}
              {vatStatus === "valid" && <span className="vat-hint valid">✓ Valid EU VAT</span>}
              {vatStatus === "invalid" && <span className="vat-hint invalid">✗ Invalid VAT ID</span>}
            </div>
          )}

          <input 
            type="text" 
            placeholder="Company Name" 
            value={formData.companyName}
            required={true}
            onChange={(e) => setFormData({...formData, companyName: e.target.value})}
          />
          
          <input 
            type="tel" 
            placeholder="Phone Number" 
            required={true}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
          />

          <input 
            type="text" 
            placeholder="Street and Number" 
            required={true}
            onChange={(e) => setFormData({...formData, street: e.target.value})}
          />
          
          <div className="input-row">
            <input 
              type="text" 
              placeholder="Postal Code" 
              required={true}
              style={{ width: "35%" }}
              onChange={(e) => setFormData({...formData, zip: e.target.value})}
            />
            <input 
              type="text" 
              placeholder="City" 
              required={true}
              style={{ width: "62%" }}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
            />
          </div>

          <input 
            type="email" 
            placeholder="Work Email" 
            required={true}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
          />

          {/* Slaptažodis 1 */}
          <div className="password-input-wrapper">
            <input 
              type={showPassword ? "text" : "password"} 
              placeholder="Password" 
              required={true}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
            />
            <button 
              type="button" 
              className="eye-btn" 
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size="20" /> : <Eye size="20" />}
            </button>
          </div>

          {/* Slaptažodis 2 */}
          <div className="password-input-wrapper">
            <input 
              type={showConfirmPassword ? "text" : "password"} 
              placeholder="Confirm Password" 
              required={true}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
            />
            <button 
              type="button" 
              className="eye-btn" 
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size="20" /> : <Eye size="20" />}
            </button>
          </div>

          {/* TEISINĖS VARNELĖS (BŪTINOS) */}
          <div className="checkbox-group legal-checkboxes">
            <label className="checkbox-item">
              <input 
                type="checkbox" 
                required={true} 
                onChange={(e) => setFormData({...formData, agb: e.target.checked})} 
              />
              <span>I accept the <a href="/agb" target="_blank" rel="noopener noreferrer">General Terms (AGB)</a></span>
            </label>
            <label className="checkbox-item">
              <input 
                type="checkbox" 
                required={true} 
                onChange={(e) => setFormData({...formData, privacy: e.target.checked})} 
              />
              <span>I accept the <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
            </label>
          </div>

          <button 
            type="submit" 
            className="submit-btn" 
            disabled={!isFormValid || isLoading}
            style={{ opacity: isFormValid ? 1 : 0.6, cursor: isFormValid ? 'pointer' : 'not-allowed' }}
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </button>
        </form>
        <p className="login-link">Already have an account? <a href="/login">Login</a></p>
      </div>
    </div>
  );
};

export default RegisterPage;